import React, { useCallback, useMemo } from 'react';

import { type MessageDescriptor, useIntl } from 'react-intl-next';

import { fg } from '@atlaskit/platform-feature-flags';
import {
	type InteractionSource,
	useAnalytics,
} from '@atlassian/conversation-assistant-instrumentation';
import { type Agent } from '@atlassian/conversation-assistant-service';
import { ChatPill } from '@atlassian/conversation-assistant-ui-components';

import { messages } from './messages';

type AgentConversationStartersProps = {
	agent: Agent | undefined;
	interactionSource: InteractionSource;
	onConversationStarterClick: (conversationStarter: string) => void;
};

export const getConversationStarters = (
	agent: Agent | undefined,
): {
	userDefinedConversationStarters: string[];
	customAgentConversationStarters: MessageDescriptor[];
	defaultAgentConversationStarters: MessageDescriptor[];
	combinedConversationStarters: Array<string | MessageDescriptor>;
} => {
	const customAgentConversationStarters = [
		messages.agentEmptyStateSuggestion1,
		messages.agentEmptyStateSuggestion2,
		messages.agentEmptyStateSuggestion3,
	];

	const userDefinedConversationStarters = agent?.user_defined_conversation_starters ?? [];

	const defaultAgentConversationStarters = [
		messages.emptyStateSuggestion1,
		messages.emptyStateSuggestion2,
		messages.emptyStateSuggestion3,
	];

	const getCombinedConversationStarters = () => {
		if (agent && !agent.is_default && fg('rovo_chat_new_create_agent')) {
			// Return default suggestions + user defined suggestions with a max of 3 suggestions
			return [
				...customAgentConversationStarters.slice(0, 3 - userDefinedConversationStarters.length),
				...userDefinedConversationStarters,
			];
		}

		return defaultAgentConversationStarters;
	};

	return {
		userDefinedConversationStarters,
		customAgentConversationStarters,
		defaultAgentConversationStarters,
		combinedConversationStarters: getCombinedConversationStarters(),
	};
};

export const AgentConversationStarters = ({
	agent,
	interactionSource,
	onConversationStarterClick,
}: AgentConversationStartersProps) => {
	const { sendAnalyticsEvent, trackAIMAUEvent } = useAnalytics();
	const { formatMessage } = useIntl();

	const { combinedConversationStarters } = useMemo(() => getConversationStarters(agent), [agent]);

	const onClick = useCallback(
		(message: string) => {
			sendAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: interactionSource,
			});

			trackAIMAUEvent({
				type: 'initiated',
				proactive: true,
				subjectId: interactionSource,
			});

			onConversationStarterClick(message);
		},
		[interactionSource, onConversationStarterClick, sendAnalyticsEvent, trackAIMAUEvent],
	);

	return (
		<>
			{combinedConversationStarters.map((starter) => {
				const translatedMessage = typeof starter === 'string' ? starter : formatMessage(starter);
				return (
					<ChatPill key={translatedMessage} onClick={() => onClick(translatedMessage)}>
						{translatedMessage}
					</ChatPill>
				);
			})}
		</>
	);
};
